import { useState } from 'react';
import { useLocation, Navigate, Link } from 'react-router-dom';
import { ApiError } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { requestMagicLink } from '../api/auth';
import { UserPair } from '../components/ui/UserChip';
import { Logo } from '../components/Logo';

// Magic-link inloggning. PIN-flödet är borttaget från frontend — det enda
// stället PIN finns kvar är portfolio-password-protection (separat flöde
// på publika portfolio-sidor, inte här).
//
// Flöde:
//   1) User skriver in email → klick på "Send sign-in link"
//   2) Backend skapar token, mailer via SES
//   3) UI visar "Check your email" med en lugn confirmation
//   4) User klickar länken i mejlet → /auth/m/:token konsumeras av
//      <MagicLinkConsumer>, sätter cookie, redirectar till app
//
// Felhantering: 429 (rate-limit) visas tydligt. 400 (bad email) också.
// Alla andra fel renderas som "Couldn't send link — try again".

interface LocationState {
  from?: string;
}

export default function Login() {
  const location = useLocation();
  const { user, isLoading, isAuthenticated } = useAuth();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const target = (location.state as LocationState | null)?.from || '/';

  if (!isLoading && isAuthenticated && user) {
    return <Navigate to={target} replace />;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !trimmed.includes('@')) {
      setErrorMsg('Please enter a valid email');
      return;
    }
    setSubmitting(true);
    setErrorMsg(null);
    try {
      await requestMagicLink(trimmed);
      setSent(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setErrorMsg('Too many attempts — try again in a few minutes');
      } else {
        setErrorMsg('Could not send link. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen min-h-[100dvh] flex items-center justify-center relative overflow-hidden">
      {/* Ambient bakgrund — radial gradients */}
      <div
        className="fixed inset-0 z-0 pointer-events-none"
        style={{
          background: [
            'radial-gradient(ellipse at 30% 20%, rgba(95,168,211,0.12), transparent 50%)',
            'radial-gradient(ellipse at 70% 80%, rgba(230,57,70,0.12), transparent 50%)',
            'radial-gradient(ellipse at center, rgba(0,0,0,0.6), rgba(0,0,0,0.95))',
          ].join(','),
        }}
      />

      {/* Card */}
      <div
        className={[
          'relative z-10 w-[440px] max-w-[calc(100vw-32px)]',
          'rounded-[28px] px-8 sm:px-11 pt-12 pb-10',
          'bg-[rgba(15,15,15,0.7)] backdrop-blur-[40px]',
          'border border-white/[0.08]',
          'shadow-[0_30px_80px_-10px_rgba(0,0,0,0.8),inset_0_1px_0_rgba(255,255,255,0.08)]',
        ].join(' ')}
      >
        {/* Brand mark — använd den delade Logo-komponenten så alla
            renderingar av "Vuelette." är pixel-identiska. */}
        <div className="flex justify-center mb-1.5">
          <Logo size="lg" color="#ededed" />
        </div>
        <div className="text-center text-[11px] text-loupe-text-3 tracking-[0.3em] uppercase mb-10">
          Curation studio · Sign in
        </div>

        {!sent ? (
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <label className="block">
              <span className="block text-[11px] text-loupe-text-3 tracking-[0.15em] uppercase mb-2">
                Email
              </span>
              <input
                type="email"
                inputMode="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                disabled={submitting}
                className="w-full bg-loupe-bg-2 border border-loupe-border rounded-xl px-4 py-3.5 text-loupe-text placeholder:text-loupe-text-3 outline-none focus:border-loupe-text-3 transition-colors min-h-[48px]"
                autoFocus
              />
            </label>

            <button
              type="submit"
              disabled={submitting || !email.trim()}
              className={[
                'w-full rounded-full px-5 py-3.5 text-sm font-semibold tracking-wider uppercase transition-all min-h-[48px]',
                submitting || !email.trim()
                  ? 'bg-white/25 text-black/40 cursor-not-allowed'
                  : 'bg-white text-black hover:scale-[1.01]',
              ].join(' ')}
            >
              {submitting ? 'Sending…' : 'Send sign-in link'}
            </button>

            {errorMsg ? (
              <div className="text-xs text-michelle text-center tracking-wide">
                {errorMsg}
              </div>
            ) : null}

            <p className="text-[11px] text-loupe-text-3 text-center mt-1 leading-relaxed">
              We'll email you a magic link.
              <br />
              No password to remember.
            </p>
          </form>
        ) : (
          <div className="text-center py-2">
            <div
              className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-loupe-bg-3 border border-loupe-border-2 mb-5 text-2xl"
              aria-hidden
            >
              ✉
            </div>
            <h2 className="font-display text-2xl mb-2.5 tracking-[-0.02em]">
              Check your email
            </h2>
            <p className="text-sm text-loupe-text-2 leading-relaxed mb-7 px-2">
              We've sent a sign-in link to{' '}
              <strong className="text-loupe-text">{email}</strong>.
              <br />
              Click the link to continue. It expires in 15 minutes.
            </p>
            <button
              type="button"
              onClick={() => {
                setSent(false);
                setEmail('');
                setErrorMsg(null);
              }}
              className="text-[11px] text-loupe-text-3 hover:text-loupe-text tracking-[0.15em] uppercase transition-colors py-2 px-3"
            >
              Use a different email
            </button>
          </div>
        )}

        <div className="mt-8 pt-6 border-t border-white/[0.06] flex justify-between items-center text-[11px] text-loupe-text-3 tracking-[0.05em]">
          <UserPair size="md" />
          <Link
            to="/login/pin"
            className="text-loupe-text-3 hover:text-loupe-text-2 transition-colors py-2 px-1 min-h-[36px] tracking-[0.15em] uppercase"
            title="PIN sign-in (backup)"
          >
            Use PIN →
          </Link>
        </div>
      </div>
    </div>
  );
}
